import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
/* eslint-disable react/no-unknown-property -- project-standard Cypress selectors */
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PropTypes from "prop-types";
import React from "react";
import { realOrSimulatedNow } from "../../shift";
import { statusMetadata } from "../ui/status_metadata";
import { appointmentUnavailableToPeer } from "./appointment_request_simple_display";
import {
  appointmentClient,
  appointmentStableKey,
  appointmentTime,
  appointmentTimestamp,
  formatDateKey,
  hasTimeOffOnDay
} from "./appointment_requests_calendar_utils";

const START_HOUR = 6;
const END_HOUR = 23;
const HOUR_HEIGHT = 64;
const EVENT_HEIGHT = 52;
const EVENT_GAP = 3;
const START_MINUTES = START_HOUR * 60;
const END_MINUTES = END_HOUR * 60;
const TIMELINE_HEIGHT = (END_HOUR - START_HOUR) * HOUR_HEIGHT;
const HOURS = Array.from({ length: END_HOUR - START_HOUR + 1 }, (_, index) => START_HOUR + index);

const formatHour = (hour) => {
  const normalized = hour % 12 || 12;
  return `${normalized} ${hour < 12 ? "AM" : "PM"}`;
};

const fullDateLabel = (day) =>
  day.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric"
  });

const rangeLabel = (days) => {
  const first = days[0];
  const last = days[days.length - 1];
  const sameMonth =
    first.getMonth() === last.getMonth() && first.getFullYear() === last.getFullYear();
  if (sameMonth) {
    const month = first.toLocaleDateString("en-US", { month: "short" });
    return `${month} ${first.getDate()}–${last.getDate()}, ${last.getFullYear()}`;
  }

  const firstLabel = first.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const lastLabel = last.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
  return `${firstLabel} – ${lastLabel}`;
};

const layoutTimedAppointments = (appointments) => {
  let nextAvailableTop = 0;

  return appointments.map((appointment, index) => {
    const minutes = appointmentTimestamp(appointment);
    const naturalTop = ((minutes - START_MINUTES) / 60) * HOUR_HEIGHT;
    const top = Math.max(naturalTop, nextAvailableTop);
    nextAvailableTop = top + EVENT_HEIGHT + EVENT_GAP;
    return { appointment, index, top };
  });
};

const ThreeDayTimeline = ({
  days,
  appointmentsByDay,
  peerSchedules,
  userData,
  selectedDayKey,
  onOpenDay,
  onPrevious,
  onNext
}) => {
  const todayKey = formatDateKey(realOrSimulatedNow());

  return (
    <div
      className="peer_dashboard_three_day_timeline"
      explorator_test_id="peer-dashboard-three-day-timeline"
    >
      <div className="peer_dashboard_three_day_navigation">
        <button
          type="button"
          className="ccw-button ccw-button--icon peer_dashboard_three_day_navigation_button"
          aria-label="Previous three days"
          explorator_test_id="peer-dashboard-three-day-previous"
          onClick={onPrevious}
        >
          <ChevronLeftIcon fontSize="small" />
        </button>
        <p aria-live="polite">{rangeLabel(days)}</p>
        <button
          type="button"
          className="ccw-button ccw-button--icon peer_dashboard_three_day_navigation_button"
          aria-label="Next three days"
          explorator_test_id="peer-dashboard-three-day-next"
          onClick={onNext}
        >
          <ChevronRightIcon fontSize="small" />
        </button>
      </div>

      <div className="peer_dashboard_three_day_headers" role="row">
        <div className="peer_dashboard_three_day_corner" aria-hidden="true" />
        {days.map((day) => {
          const dateKey = formatDateKey(day);
          const appointments = appointmentsByDay[dateKey] || [];
          const timeOff = hasTimeOffOnDay(peerSchedules, day);
          const conflict = timeOff && appointments.length > 0;
          const isToday = todayKey === dateKey;

          return (
            <button
              key={dateKey}
              type="button"
              className={`peer_dashboard_three_day_column_header${
                selectedDayKey === dateKey ? " is-selected" : ""
              }${isToday ? " is-today" : ""}`}
              aria-label={`${fullDateLabel(day)}, ${appointments.length} appointment${
                appointments.length === 1 ? "" : "s"
              }${timeOff ? (conflict ? ", time-off conflict" : ", time off") : ""}`}
              aria-current={isToday ? "date" : undefined}
              explorator_test_id={`peer-dashboard-three-day-date-${dateKey}`}
              onClick={() => onOpenDay(dateKey)}
            >
              <span>{day.toLocaleDateString("en-US", { weekday: "short" })}</span>
              <strong>{day.getDate()}</strong>
            </button>
          );
        })}
      </div>

      <div className="peer_dashboard_three_day_all_day" role="row">
        <div className="peer_dashboard_three_day_all_day_label">All day</div>
        {days.map((day) => {
          const dateKey = formatDateKey(day);
          const appointments = appointmentsByDay[dateKey] || [];
          const timeOff = hasTimeOffOnDay(peerSchedules, day);
          const conflict = timeOff && appointments.length > 0;
          const outsideTimeline = appointments.filter((appointment) => {
            const minutes = appointmentTimestamp(appointment);
            return (
              appointment.whole_day_reservation || minutes < START_MINUTES || minutes >= END_MINUTES
            );
          });

          return (
            <div key={dateKey} className="peer_dashboard_three_day_all_day_cell" role="gridcell">
              {timeOff && (
                <button
                  type="button"
                  className={`peer_dashboard_three_day_all_day_item ${
                    conflict ? "is-conflict" : "is-time-off"
                  }`}
                  explorator_test_id={`peer-dashboard-three-day-time-off-${dateKey}`}
                  onClick={() => onOpenDay(dateKey)}
                >
                  {conflict ? "⚠ Conflict" : "— Time off"}
                </button>
              )}
              {outsideTimeline.map((appointment, index) => {
                const status = statusMetadata(appointment.status);
                return (
                  <button
                    key={appointmentStableKey(appointment, index)}
                    type="button"
                    className={`peer_dashboard_three_day_all_day_item ${status.className}`}
                    aria-label={`${appointmentTime(appointment)}, ${appointmentClient(
                      appointment
                    )}, ${status.label}. Open ${fullDateLabel(day)}.`}
                    explorator_test_id={`peer-dashboard-three-day-appointment-${appointmentStableKey(
                      appointment,
                      index
                    )}`}
                    onClick={() => onOpenDay(dateKey)}
                  >
                    <span>{appointmentTime(appointment)}</span>
                    <strong>{appointmentClient(appointment)}</strong>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>

      <div className="peer_dashboard_three_day_timeline_body">
        <div className="peer_dashboard_three_day_time_rail" style={{ height: TIMELINE_HEIGHT }}>
          {HOURS.map((hour) => (
            <span key={hour} style={{ top: (hour - START_HOUR) * HOUR_HEIGHT }}>
              {formatHour(hour)}
            </span>
          ))}
        </div>
        <div className="peer_dashboard_three_day_columns" style={{ height: TIMELINE_HEIGHT }}>
          {days.map((day) => {
            const dateKey = formatDateKey(day);
            const appointments = (appointmentsByDay[dateKey] || []).filter((appointment) => {
              const minutes = appointmentTimestamp(appointment);
              return (
                !appointment.whole_day_reservation &&
                minutes >= START_MINUTES &&
                minutes < END_MINUTES
              );
            });
            const timeOff = hasTimeOffOnDay(peerSchedules, day);
            const conflict = timeOff && appointments.length > 0;

            return (
              <div
                key={dateKey}
                className={`peer_dashboard_three_day_column${timeOff ? " is-time-off" : ""}${
                  conflict ? " is-conflict" : ""
                }`}
                data-date-key={dateKey}
                role="gridcell"
              >
                {layoutTimedAppointments(appointments).map(({ appointment, index, top }) => {
                  const status = statusMetadata(appointment.status);
                  const recurring = appointment.is_recurring || appointment.recurring;
                  const unavailable =
                    userData.account_type === "Peer" &&
                    appointmentUnavailableToPeer(appointment, userData.id);
                  const visibleStatus = unavailable ? "Unavailable to you" : status.label;

                  return (
                    <button
                      key={appointmentStableKey(appointment, index)}
                      type="button"
                      className={`peer_dashboard_three_day_timed_item ${status.className}${
                        unavailable ? " is-unavailable" : ""
                      }`}
                      style={{ height: EVENT_HEIGHT, top }}
                      aria-label={`${appointmentTime(appointment)}, ${appointmentClient(
                        appointment
                      )}, ${visibleStatus}${recurring ? ", recurring" : ""}. Open ${fullDateLabel(
                        day
                      )}.`}
                      onClick={() => onOpenDay(dateKey)}
                      explorator_test_id={`peer-dashboard-three-day-appointment-${appointmentStableKey(
                        appointment,
                        index
                      )}`}
                      title={`${appointmentTime(appointment)} · ${appointmentClient(
                        appointment
                      )} · ${visibleStatus}`}
                    >
                      <span className="peer_dashboard_three_day_timed_item_time">
                        {appointmentTime(appointment)}
                      </span>
                      <span className="peer_dashboard_three_day_timed_item_client">
                        {appointmentClient(appointment)}
                      </span>
                      {recurring && <span aria-hidden="true">↻</span>}
                      <span aria-hidden="true">{unavailable ? "—" : status.symbol}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

ThreeDayTimeline.propTypes = {
  days: PropTypes.arrayOf(PropTypes.instanceOf(Date)).isRequired,
  appointmentsByDay: PropTypes.object.isRequired,
  peerSchedules: PropTypes.array,
  userData: PropTypes.object.isRequired,
  selectedDayKey: PropTypes.string,
  onOpenDay: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired
};

ThreeDayTimeline.defaultProps = {
  peerSchedules: [],
  selectedDayKey: null
};

export default ThreeDayTimeline;
